import { Controller } from "@hotwired/stimulus"

// Mounted on the hero <section> in admin_edit mode by
// InnerBodyAdminChromeHelper#inner_body_hero_anchor_attrs. Click opens a
// small floating editor and PATCHes the page via `patchUrlValue` (the
// admin_page_path for this page).
//
// v1 scope: Summary-only inline editor. Title edits are not surfaced here
// — admins rename pages via the legacy form (sidebar in themed edit view)
// because slug regeneration (Page#set_slug before_save) would 404 the
// in-flight Turbo.visit back to the old edit URL. v2 should accept a new
// edit URL from the PATCH response so we can safely round-trip renames.
//
// pageId is intentionally NOT a Stimulus value here: the slug is already
// encoded in the patch URL, so passing it twice would just be a footgun
// for drift. Matches the helper, which also drops `data-page-id`.
export default class extends Controller {
  static values = { patchUrl: String }

  connect() {
    this.element.style.cursor = "pointer"
    // Bind once so disconnect() can remove the exact same listener — Turbo
    // cache restore reconnects controllers and otherwise stacks handlers.
    this._boundOpen = this._open.bind(this)
    this.element.addEventListener("click", this._boundOpen, { capture: true })
  }

  disconnect() {
    this.element.removeEventListener("click", this._boundOpen, { capture: true })
    // If Turbo evicts us while the overlay is open, tear it down too so the
    // document-level ESC listener doesn't outlive the controller.
    this._closeOverlay?.()
  }

  async _open(event) {
    // Don't hijack clicks on real interactive descendants (nav links,
    // buttons, form fields living inside the hero).
    if (event.target.closest("a, button, input, textarea")) return
    event.preventDefault()
    // Re-entry guard: don't stack overlays on rapid double-clicks.
    if (this._closeOverlay) return

    const lede  = this.element.querySelector(".sh-journal-lede")?.textContent?.trim() || ""

    const overlay = document.createElement("div")
    overlay.className = "sh-admin-anchor-editor"
    overlay.innerHTML = `
      <label>Summary<textarea data-field="summary">${this._esc(lede)}</textarea></label>
      <div class="actions">
        <button type="button" data-action="cancel">Cancel</button>
        <button type="button" data-action="save">Save</button>
      </div>
    `
    document.body.appendChild(overlay)

    // ESC closes the overlay. Listener is removed on close so it doesn't
    // leak across successive opens. closeOverlay is stashed on the
    // instance so disconnect() can fire it too if Turbo tears us down
    // while the overlay is still up.
    // TODO(v2): also dismiss on outside click for parity with other popovers.
    const escHandler = (e) => {
      if (e.key === "Escape") closeOverlay()
    }
    const closeOverlay = () => {
      overlay.remove()
      document.removeEventListener("keydown", escHandler)
      this._closeOverlay = null
    }
    this._closeOverlay = closeOverlay
    document.addEventListener("keydown", escHandler)

    overlay.querySelector("[data-action=cancel]").onclick = closeOverlay
    overlay.querySelector("[data-action=save]").onclick = async () => {
      const summaryVal = overlay.querySelector('[data-field=summary]').value
      const csrfMeta = document.querySelector("meta[name=csrf-token]")
      const csrf = csrfMeta?.content
      if (!csrf) { closeOverlay(); return }
      const res = await fetch(this.patchUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf, Accept: "text/vnd.turbo-stream.html" },
        body: JSON.stringify({ page: { summary: summaryVal } })
      })
      if (!res.ok) { alert("Couldn't save your changes."); return }
      closeOverlay()
      if (window.Turbo) {
        window.Turbo.visit(window.location.href, { action: "replace" })
      } else {
        window.location.reload()
      }
    }

    // Land focus on the summary textarea so the admin can type immediately.
    overlay.querySelector('[data-field=summary]').focus()
  }

  _esc(s) { return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;") }
}
